/**
 * Staging-only Owner video-pool review requests: the review queue entry and
 * decision shapes, the queue read and the review submission.
 */

import { request } from './request.js';

export type PoolRightsReviewState = 'REFERENCE_APPROVED' | 'REJECTED' | 'PENDING';
export type PoolSafetyReviewState = 'APPROVED' | 'REJECTED' | 'PENDING';
export type PoolIdentityReviewState =
  'CONFIRMED_DISTINCT' | 'CONFIRMED_SAME_AS' | 'UNCERTAIN_REVIEW';

export interface PoolReviewQueueEntry {
  readonly poolEntryId: string;
  readonly editionId: string;
  readonly anatomyReference: string | null;
  readonly identityState: string | null;
  readonly latestQualification: {
    readonly status: 'QUALIFIED' | 'DISQUALIFIED' | 'PENDING';
    readonly evaluatedAt: string;
    readonly gates: Readonly<
      Record<
        string,
        {
          readonly state: 'PASS' | 'FAIL' | 'UNKNOWN' | 'PENDING';
          readonly note: string | null;
        }
      >
    >;
  } | null;
  readonly latestReview: {
    readonly reviewedAt: string;
    readonly rightsReference: { readonly state: PoolRightsReviewState };
    readonly safety: { readonly state: PoolSafetyReviewState };
    readonly identity: { readonly state: PoolIdentityReviewState };
  } | null;
  readonly occurrences: readonly {
    readonly occurrenceId: string;
    readonly platform: string;
    readonly canonicalUrl: string;
    readonly publisherHandle: string | null;
    readonly publisherDisplayName: string | null;
  }[];
}

export interface PoolReviewDecisionBody {
  readonly reviewId: string;
  readonly poolEntryId: string;
  readonly editionId: string;
  readonly rightsReference: {
    readonly state: PoolRightsReviewState;
    readonly evidence: readonly { readonly kind: 'URL'; readonly reference: string }[];
    readonly note: string;
  };
  readonly safety: {
    readonly state: PoolSafetyReviewState;
    readonly evidence: readonly { readonly kind: 'URL'; readonly reference: string }[];
    readonly note: string;
  };
  readonly identity: {
    readonly state: PoolIdentityReviewState;
    readonly sameAsEditionId: string | null;
    readonly evidence: readonly { readonly kind: 'URL'; readonly reference: string }[];
    readonly note: string;
  };
}

export function fetchPoolReviewQueue(limit = 40): Promise<{
  readonly entries: readonly PoolReviewQueueEntry[];
}> {
  return request('GET', `/admin/video-pool/review-queue?limit=${String(limit)}`);
}

export function submitPoolReview(body: PoolReviewDecisionBody): Promise<{
  readonly qualification: { readonly status: string };
}> {
  return request('POST', '/admin/video-pool/reviews', { body });
}
